// frontend/src/components/widgets/WebContentWidget.js - OPTIMIZED WITH RESPONSIVE + THEMES
import React, { useState, useEffect, useRef } from 'react';
import { Globe, RefreshCw, AlertCircle, Check } from 'lucide-react';

const WebContentWidget = ({ config, preview = false, screenSize = 'FHD' }) => {
  const [mode, setMode] = useState('loading');
  const [screenshotUrl, setScreenshotUrl] = useState(null);
  const [error, setError] = useState(null);
  const [lastRefresh, setLastRefresh] = useState(Date.now());
  const iframeRef = useRef(null);
  const retryTimeoutRef = useRef(null);

  const {
    url,
    displayMode = 'auto',
    refreshInterval = 300,
    zoom = 100,
    scrollPosition = 'top',
    theme = 'light'
  } = config;

  // ✅ RESPONSIVE: Get multipliers based on screen size
  const getResponsiveMultipliers = () => {
    const multipliers = {
      SM: { font: 0.4, spacing: 0.45, icon: 0.5 },
      MD: { font: 0.6, spacing: 0.65, icon: 0.7 },
      HD: { font: 0.8, spacing: 0.85, icon: 0.85 },
      FHD: { font: 1.0, spacing: 1.0, icon: 1.0 },
      QHD: { font: 1.15, spacing: 1.1, icon: 1.1 },
      '4K': { font: 1.4, spacing: 1.3, icon: 1.3 }
    };
    return multipliers[screenSize] || multipliers.FHD;
  };

  const m = getResponsiveMultipliers();

  // ✅ GET THEME STYLES
  const getThemeStyles = () => {
    const themes = {
      light: {
        background: 'bg-gradient-to-br from-gray-50 to-gray-100',
        overlayBg: 'bg-white/90',
        badgeBg: 'bg-green-500',
        text: 'text-gray-900',
        textSecondary: 'text-gray-600'
      },
      dark: {
        background: 'bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900',
        overlayBg: 'bg-gray-900/90',
        badgeBg: 'bg-green-600',
        text: 'text-white',
        textSecondary: 'text-gray-300'
      },
      blue: {
        background: 'bg-gradient-to-br from-[#3b82f6] via-[#2563eb] to-[#1d4ed8]',
        overlayBg: 'bg-blue-900/90',
        badgeBg: 'bg-white',
        text: 'text-white',
        textSecondary: 'text-blue-100'
      },
      classicBlue: {
        background: 'bg-gradient-to-br from-[#1e3c72] via-[#2a5298] to-[#1e3c72]',
        overlayBg: 'bg-blue-900/90',
        badgeBg: 'bg-white',
        text: 'text-white',
        textSecondary: 'text-blue-100'
      },
      green: {
        background: 'bg-gradient-to-br from-green-600 via-emerald-600 to-teal-700',
        overlayBg: 'bg-green-900/90',
        badgeBg: 'bg-white',
        text: 'text-white',
        textSecondary: 'text-green-100'
      },
      minimal: {
        background: 'bg-white',
        overlayBg: 'bg-white/95',
        badgeBg: 'bg-gray-900',
        text: 'text-gray-900',
        textSecondary: 'text-gray-600'
      }
    };
    return themes[theme] || themes.light;
  };

  const themeStyles = getThemeStyles();

  useEffect(() => {
    if (!url) {
      setError('No URL configured');
      setMode('error');
      return;
    }

    // Validate URL
    try {
      new URL(url);
    } catch {
      setError('Invalid URL format');
      setMode('error');
      return;
    }

    // Determine display mode
    if (displayMode === 'screenshot') {
      loadScreenshot();
    } else if (displayMode === 'iframe') {
      setMode('iframe');
    } else {
      tryIframe();
    }

    // Setup refresh interval
    if (!preview) {
      const interval = setInterval(() => {
        setLastRefresh(Date.now());
        if (mode === 'screenshot') {
          loadScreenshot();
        }
      }, refreshInterval * 1000);

      return () => clearInterval(interval);
    }
  }, [url, displayMode, refreshInterval, preview]);

  const tryIframe = () => {
    setMode('iframe');
    
    retryTimeoutRef.current = setTimeout(() => {
      console.warn('⚠️ iframe timeout, falling back to screenshot');
      fallbackToScreenshot();
    }, 5000);
  };

  const fallbackToScreenshot = async () => {
    console.log('🔄 Falling back to screenshot mode');
    clearTimeout(retryTimeoutRef.current);
    await loadScreenshot();
  };

  const loadScreenshot = async () => {
    try {
      setMode('loading');
      
      const token = localStorage.getItem('token');
      const apiUrl = process.env.REACT_APP_API_URL || 'https://app.alpenscreen.ch/api';
      
      const response = await fetch(`${apiUrl}/web-content/screenshot`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          url,
          zoom,
          scrollPosition,
          quality: 90
        })
      });

      if (!response.ok) {
        throw new Error('Failed to generate screenshot');
      }

      const data = await response.json();
      
      // ✅ FIX: Handle both HTTP and HTTPS properly
      const baseUrl = apiUrl.replace('/api', '');
      const fullUrl = `${baseUrl}${data.screenshotUrl}`;
      
      setScreenshotUrl(fullUrl);
      setMode('screenshot');
      setError(null);

    } catch (err) {
      console.error('❌ Error loading screenshot:', err);
      setError(err.message);
      setMode('error');
    }
  };

  const handleIframeLoad = () => {
    clearTimeout(retryTimeoutRef.current);
    console.log('✅ iframe loaded successfully');
  };

  const handleIframeError = () => {
    console.warn('❌ iframe failed to load, falling back to screenshot');
    fallbackToScreenshot();
  };

  // ✅ LOADING STATE - RESPONSIVE
  if (mode === 'loading') {
    return (
      <div className={`flex items-center justify-center h-full ${themeStyles.background}`}>
        <div className="text-center" style={{ padding: `${64 * m.spacing}px` }}>
          <RefreshCw 
            className={`animate-spin mx-auto ${themeStyles.text}`}
            size={48 * m.icon}
            style={{ marginBottom: `${32 * m.spacing}px` }}
          />
          <p 
            className={`font-semibold ${themeStyles.text}`}
            style={{ fontSize: `${24 * m.font}px` }}
          >
            Loading content...
          </p>
          <p 
            className={`${themeStyles.textSecondary}`}
            style={{ 
              fontSize: `${14 * m.font}px`,
              marginTop: `${16 * m.spacing}px`
            }}
          >
            {url}
          </p>
        </div>
      </div>
    );
  }

  // ✅ ERROR STATE - RESPONSIVE
  if (mode === 'error') {
    return (
      <div className={`flex items-center justify-center h-full ${themeStyles.background}`}>
        <div 
          className="text-center"
          style={{ 
            maxWidth: '600px',
            padding: `${64 * m.spacing}px`
          }}
        >
          <AlertCircle 
            className="mx-auto text-red-600"
            size={48 * m.icon}
            style={{ marginBottom: `${32 * m.spacing}px` }}
          />
          <h3 
            className={`font-bold ${themeStyles.text}`}
            style={{ 
              fontSize: `${28 * m.font}px`,
              marginBottom: `${16 * m.spacing}px`
            }}
          >
            Unable to Load Content
          </h3>
          <p 
            className={themeStyles.textSecondary}
            style={{ 
              fontSize: `${18 * m.font}px`,
              marginBottom: `${32 * m.spacing}px`
            }}
          >
            {error}
          </p>
          <p 
            className={themeStyles.textSecondary}
            style={{ fontSize: `${14 * m.font}px` }}
          >
            {url}
          </p>
        </div>
      </div>
    );
  }

  // ✅ IFRAME MODE - RESPONSIVE
  if (mode === 'iframe') {
    return (
      <div className="relative w-full h-full bg-white">
        {preview && (
          <div 
            className={`absolute z-10 ${themeStyles.badgeBg} text-white rounded-full font-semibold flex items-center`}
            style={{
              top: `${32 * m.spacing}px`,
              left: `${32 * m.spacing}px`,
              gap: `${16 * m.spacing}px`,
              padding: `${12 * m.spacing}px ${24 * m.spacing}px`,
              fontSize: `${14 * m.font}px`
            }}
          >
            <Check size={16 * m.icon} />
            <span>iframe Mode</span>
          </div>
        )}
        <iframe
          ref={iframeRef}
          src={url}
          className="w-full h-full border-0"
          style={{
            transform: zoom !== 100 ? `scale(${zoom / 100})` : 'none',
            transformOrigin: 'top left',
            width: zoom !== 100 ? `${10000 / zoom}%` : '100%',
            height: zoom !== 100 ? `${10000 / zoom}%` : '100%'
          }}
          onLoad={handleIframeLoad}
          onError={handleIframeError}
          sandbox="allow-same-origin allow-scripts allow-popups allow-forms"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        />
      </div>
    );
  }

  // ✅ SCREENSHOT MODE - RESPONSIVE
  if (mode === 'screenshot' && screenshotUrl) {
    return (
      <div className={`relative w-full h-full ${themeStyles.background} flex items-center justify-center overflow-hidden`}>
        {preview && (
          <div 
            className={`absolute z-10 ${themeStyles.badgeBg} text-white rounded-full font-semibold flex items-center`}
            style={{
              top: `${32 * m.spacing}px`,
              left: `${32 * m.spacing}px`,
              gap: `${16 * m.spacing}px`,
              padding: `${12 * m.spacing}px ${24 * m.spacing}px`,
              fontSize: `${14 * m.font}px`
            }}
          >
            <Globe size={16 * m.icon} />
            <span>Screenshot Mode</span>
          </div>
        )}
        <img
          src={screenshotUrl}
          alt="Web content screenshot"
          className="max-w-full max-h-full object-contain"
          style={{
            transform: zoom !== 100 ? `scale(${zoom / 100})` : 'none'
          }}
        />
        {!preview && (
          <div 
            className={`absolute ${themeStyles.overlayBg} rounded`}
            style={{
              bottom: `${32 * m.spacing}px`,
              right: `${32 * m.spacing}px`,
              fontSize: `${12 * m.font}px`,
              padding: `${8 * m.spacing}px ${16 * m.spacing}px`
            }}
          >
            <span className={themeStyles.textSecondary}>
              Last updated: {new Date(lastRefresh).toLocaleTimeString()}
            </span>
          </div>
        )}
      </div>
    );
  }

  return null;
};

export default WebContentWidget;